<template>
  <div class="filter flex justify-start items-center gap-4">
    <NListbox
      v-model:query="query"
      filterable
      has-fixed-item
      class="w-44"
      input-size="mini"
      :model-value="selectedIsin"
      :fixed-title="t('option.selectedUnderlying')"
      :dropdown-title="dropdownTitle"
      :empty-state-text="t('option.chainNotFound', { name: query })"
      :placeholder="t('option.selectUnderlying')"
      @update:model-value="handleUpdateIsin"
      :query-transformer="(q) => translate(q)"
      :disabled="!underlyingAssetIsins?.length"
      clearable
    >
      <NListboxOption
        v-for="item in underlyingAssetIsins"
        :key="item.isin"
        :value="item.isin"
        :label="item.instrumentName"
      >
        <div class="flex justify-start items-center gap-1">
          <SymbolTradeStateIcon
            class="me-1"
            size="medium"
            :trading-state-code="item.instrumentTradingState"
          />
          <NText weight="bold" variant="caption2">
            {{ item.instrumentName }}
          </NText>
          <NText variant="caption2">
            <NumberFormatter
              #="{ number }"
              :value="item.referencePriceVariationPercentage / 100"
              number-style="percent"
              maximum-fraction-digits="2"
            >
              <ChangePriceEffect
                :price="item.referencePriceVariationPercentage"
              >
                <NText
                  dir="ltr"
                  variant="caption2"
                  weight="medium"
                  :class="{
                    'text-brand-primary':
                      item.referencePriceVariationPercentage > 0,
                    'text-critical-primary':
                      item.referencePriceVariationPercentage < 0,
                  }"
                  class="ms-1"
                >
                  ({{ number }})
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
          </NText>
          <NText variant="caption2">
            <NumberFormatter #="{ number }" :value="item.lastTradePrice">
              <ChangePriceEffect :price="item.lastTradePrice">
                <NText
                  :key="item.lastTradePrice"
                  variant="caption2"
                  weight="medium"
                  dir="ltr"
                >
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
          </NText>
        </div>
      </NListboxOption>
    </NListbox>

    <NListbox
      class="w-32"
      :model-value="viewMode"
      input-size="mini"
      :disabled="!underlyingAssetIsinEndDates?.endDates?.length"
      @update:model-value="handleUpdateViewMode"
    >
      <NListboxOption
        v-for="item in Object.keys(CONTRACT_CHAIN_SIDE)"
        :key="item"
        :value="+item"
        :label="CONTRACT_CHAIN_SIDE[item]"
        :disabled="!underlyingAssetIsinEndDates?.endDates?.length"
      >
        <NText variant="caption2">{{ CONTRACT_CHAIN_SIDE[item] }}</NText>
      </NListboxOption>
    </NListbox>

    <div class="flex justify-start items-center gap-1">
      <NButtonIcon
        :disabled="disabledPrev"
        class="bg-neutral-primary disabled:bg-neutral-secondary disabled:border-neutral-tertiary disabled:cursor-not-allowed"
        variant="outline"
        size="xsmall"
        color="gray"
        @click.stop="onPrevHandler"
      >
        <NegarArrowRightIcon :size="20" />
      </NButtonIcon>

      <NListbox
        :model-value="endDate"
        :placeholder="t('option.dueDate')"
        input-size="mini"
        :disabled="!underlyingAssetIsinEndDates?.endDates?.length"
        @update:model-value="handleUpdateEndDate"
      >
        <NListboxOption
          v-for="(item, index) in underlyingAssetIsinEndDates?.endDates"
          :key="index"
          :value="item"
          :label="`${t('modules.portfolio.embeddedPutOption.dueDate')} ${d(
            item
          )}`"
        >
          <NText variant="caption2">{{ d(item) }}</NText>
        </NListboxOption>
      </NListbox>

      <NButtonIcon
        :disabled="disabledNext"
        class="bg-neutral-primary disabled:bg-neutral-secondary disabled:border-neutral-tertiary"
        variant="outline"
        size="xsmall"
        color="gray"
        @click.stop="onNextHandler"
      >
        <NegarArrowLeftIcon :size="20" />
      </NButtonIcon>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { CONTRACT_CHAIN_SIDE } from '@core/option';
import { NegarArrowLeftIcon, NegarArrowRightIcon } from '@negar/icons';
import {
  Instrument,
  UnderlyingAssetIsin,
  UnderlyingEndDateQuery,
} from '@libs/api-reference';
import { translate } from '@libs/utils/src';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import { SymbolTradeStateIcon, useActiveSymbolIsin } from '@/modules/symbol';

import { TViewMode } from './index.vue';

type Item = { label: string; value: string };

const { t, d } = useI18n();

const query = ref();
const { updateActiveIsin } = useActiveSymbolIsin();

const emit = defineEmits<{
  (e: 'update:viewMode', value: TViewMode): void;
  (e: 'update:endDate', value: Item): void;
}>();
const props = defineProps<{
  symbol: Instrument;
  viewMode: TViewMode;
  endDate: Item | undefined;
  selectedIsin: Item | undefined;
  underlyingAssetIsins: UnderlyingAssetIsin[] | undefined;
  underlyingAssetIsinEndDates: UnderlyingEndDateQuery | undefined;
}>();

const dropdownTitle = computed(() => {
  return props.underlyingAssetIsins?.length
    ? t('option.underlyingAssetIsinsList', {
        length: props.underlyingAssetIsins?.length,
      })
    : '';
});

const activeEndDateIndex = computed(() => {
  const currentIndex = props.underlyingAssetIsinEndDates?.endDates?.findIndex(
    (item) => item === props.endDate?.value
  );
  return typeof currentIndex === 'number' ? currentIndex : -1;
});

const disabledPrev = computed(() => {
  if (!props.underlyingAssetIsinEndDates?.endDates?.length) return true;

  if (
    !props.underlyingAssetIsinEndDates?.endDates?.length ||
    activeEndDateIndex.value < -1
  )
    return false;

  return activeEndDateIndex.value - 1 < 0;
});

const disabledNext = computed(() => {
  if (!props.underlyingAssetIsinEndDates?.endDates?.length) return true;

  if (
    !props.underlyingAssetIsinEndDates?.endDates?.length ||
    activeEndDateIndex.value < -1
  )
    return false;

  return (
    activeEndDateIndex.value + 1 >=
    props.underlyingAssetIsinEndDates?.endDates?.length
  );
});

function handleUpdateViewMode(value: unknown) {
  emit('update:viewMode', value as TViewMode);
}

function handleUpdateEndDate(value: Item) {
  emit('update:endDate', value);
}

async function handleUpdateIsin(value: Item) {
  await updateActiveIsin(value.value);
}

function onPrevHandler() {
  const prevItem =
    props.underlyingAssetIsinEndDates?.endDates?.[activeEndDateIndex.value - 1];

  if (!prevItem) return;

  emit('update:endDate', {
    label: `${t('modules.portfolio.embeddedPutOption.dueDate')} ${d(prevItem)}`,
    value: prevItem,
  });
}

function onNextHandler() {
  const prevItem =
    props.underlyingAssetIsinEndDates?.endDates?.[activeEndDateIndex.value + 1];

  if (!prevItem) return;

  emit('update:endDate', {
    label: `${t('modules.portfolio.embeddedPutOption.dueDate')} ${d(prevItem)}`,
    value: prevItem,
  });
}
</script>

<style lang="css" scoped>
.filter :deep(> div) {
  width: auto;
}
</style>
